/**
 * Employee WRMS Dashboard — Digi Sahyadri
 * Shows stats, leave balance, recent requests and quick apply buttons
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  FaClipboardList,
  FaCheck,
  FaClock,
  FaTimes,
  FaSync,
  FaSearch,
  FaThLarge,
  FaList,
  FaBan,
  FaCalendarAlt,
  FaRunning,
  FaDoorOpen,
  FaBusinessTime,
  FaUserFriends,
} from 'react-icons/fa';
import RequestCard from '../shared/RequestCard';
import { getRequestsByEmployee, getRequestsForHandover, getRequestsForApprover, approveStage, rejectRequest } from '../../services/wrmsService';
import { toast } from 'react-toastify';

const QUICK_APPLY = [
  { type: 'leave', path: '/wrms/leave-request', icon: FaCalendarAlt, label: 'Leave Request' },
  { type: 'early_leave', path: '/wrms/early-leave', icon: FaRunning, label: 'Early Leave' },
  { type: 'gate_pass', path: '/wrms/gate-pass', icon: FaDoorOpen, label: 'Gate Pass' },
  { type: 'extra_work', path: '/wrms/extra-work', icon: FaBusinessTime, label: 'Extra Work' },
];

const EmployeeWRMSDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [handoverRequests, setHandoverRequests] = useState([]);
  const [managerApprovals, setManagerApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionProcessing, setActionProcessing] = useState(null);
  
  // New Filter & Layout States
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [layoutMode, setLayoutMode] = useState('grid');

  const fetchData = useCallback(async () => {
    const userId = user?.uid || user?.id;
    if (!userId) return;
    setLoading(true);
    try {
      const [reqs, handovers, mgrApprovals] = await Promise.all([
        getRequestsByEmployee(userId),
        getRequestsForHandover(user),
        getRequestsForApprover('manager', user?.department, user),
      ]);
      setRequests(reqs);
      setHandoverRequests(handovers);
      setManagerApprovals(mgrApprovals || []);
    } catch (err) {
      toast.error('Failed to load request data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAcceptHandover = async (req) => {
    setActionProcessing(req.id);
    try {
      await approveStage(req.requestType, req.id, user, 'Handover responsibility accepted');
      toast.success(`Handover accepted for ${req.employeeName}!`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to accept handover');
    } finally {
      setActionProcessing(null);
    }
  };

  const handleDeclineHandover = async (req) => {
    const reason = window.prompt(`Please provide a reason for declining handover from ${req.employeeName}:`);
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('Reason is required to decline handover');
      return;
    }
    setActionProcessing(req.id);
    try {
      await rejectRequest(req.requestType, req.id, user, `Handover declined by colleague: ${reason}`);
      toast.warning(`Handover declined for ${req.employeeName}`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to decline handover');
    } finally {
      setActionProcessing(null);
    }
  };

  const handleApproveManagerRequest = async (req) => {
    setActionProcessing(req.id);
    try {
      await approveStage(req.requestType, req.id, user, 'Approved by Reporting Manager');
      toast.success(`Request for ${req.employeeName} approved!`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to approve request');
    } finally {
      setActionProcessing(null);
    }
  };

  const handleRejectManagerRequest = async (req) => {
    const reason = window.prompt(`Please provide a reason for rejecting request from ${req.employeeName}:`);
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionProcessing(req.id);
    try {
      await rejectRequest(req.requestType, req.id, user, reason);
      toast.warning(`Request for ${req.employeeName} rejected`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to reject request');
    } finally {
      setActionProcessing(null);
    }
  };

  const stats = {
    total: requests.length,
    approved: requests.filter(r => r.status === 'approved').length,
    pending: requests.filter(r => r.status === 'pending').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    cancelled: requests.filter(r => r.status === 'cancelled').length,
    handover: handoverRequests.length,
    manager: managerApprovals.length,
  };

  const baseList = statusFilter === 'manager'
    ? managerApprovals
    : (statusFilter === 'handover'
      ? handoverRequests
      : (statusFilter === 'all'
        ? requests
        : requests.filter(r => r.status?.toLowerCase() === statusFilter)));

  const filteredRequests = baseList;

  // Apply search & date filters
  const searchedRequests = filteredRequests.filter(req => {
    const searchLower = search.toLowerCase();
    const matchSearch = !search ||
      req.requestId?.toLowerCase().includes(searchLower) ||
      req.employeeName?.toLowerCase().includes(searchLower) ||
      req.reason?.toLowerCase().includes(searchLower) ||
      req.requestType?.toLowerCase().includes(searchLower) ||
      (req.handoverTo && req.handoverTo.toLowerCase().includes(searchLower));

    let matchDate = true;
    if (startDate || endDate) {
      const reqDate = req.submittedAt?.toDate ? req.submittedAt.toDate() : new Date(req.submittedAt);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (reqDate < start) matchDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (reqDate > end) matchDate = false;
      }
    }
    return matchSearch && matchDate;
  });

  const getHeaderLabel = () => {
    switch (statusFilter) {
      case 'manager': return `Pending Approvals as Reporting Manager (${managerApprovals.length})`;
      case 'handover': return `Handover Requests Assigned to Me (${handoverRequests.length})`;
      case 'approved': return 'My Approved Requests';
      case 'pending': return 'My Pending Requests';
      case 'rejected': return 'My Rejected Requests';
      case 'cancelled': return 'My Cancelled Requests';
      default: return 'My Recent Requests';
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {/* Stats Row */}
      <div className="wrms-stats-grid">
        <div 
          className={`wrms-stat-card stat-primary clickable ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <div className="wrms-stat-icon icon-primary"><FaClipboardList /></div>
          <div className="wrms-stat-number">{stats.total}</div>
          <div className="wrms-stat-label">Total Applied</div>
        </div>
        <div 
          className={`wrms-stat-card stat-success clickable ${statusFilter === 'approved' ? 'active' : ''}`}
          onClick={() => setStatusFilter('approved')}
        >
          <div className="wrms-stat-icon icon-success"><FaCheck /></div>
          <div className="wrms-stat-number">{stats.approved}</div>
          <div className="wrms-stat-label">Approved</div>
        </div>
        <div 
          className={`wrms-stat-card stat-warning clickable ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          <div className="wrms-stat-icon icon-warning"><FaClock /></div>
          <div className="wrms-stat-number">{stats.pending}</div>
          <div className="wrms-stat-label">Pending</div>
        </div>
        <div 
          className={`wrms-stat-card stat-danger clickable ${statusFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setStatusFilter('rejected')}
        >
          <div className="wrms-stat-icon icon-danger"><FaTimes /></div>
          <div className="wrms-stat-number">{stats.rejected}</div>
          <div className="wrms-stat-label">Rejected</div>
        </div>
        <div 
          className={`wrms-stat-card stat-cancelled clickable ${statusFilter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setStatusFilter('cancelled')}
        >
          <div className="wrms-stat-icon icon-cancelled"><FaBan /></div>
          <div className="wrms-stat-number">{stats.cancelled}</div>
          <div className="wrms-stat-label">Cancelled</div>
        </div>
        <div 
          className={`wrms-stat-card clickable ${statusFilter === 'handover' ? 'active' : ''}`}
          onClick={() => setStatusFilter('handover')}
          style={{
            background: statusFilter === 'handover' ? 'linear-gradient(135deg, #4f46e5, #4338ca)' : (handoverRequests.length > 0 ? '#eef2ff' : '#ffffff'),
            border: handoverRequests.length > 0 ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
            color: statusFilter === 'handover' ? '#ffffff' : '#3730a3'
          }}
        >
          <div className="wrms-stat-icon" style={{ background: '#4f46e5', color: '#ffffff' }}><FaUserFriends /></div>
          <div className="wrms-stat-number" style={{ color: statusFilter === 'handover' ? '#ffffff' : '#1e1b4b' }}>{handoverRequests.length}</div>
          <div className="wrms-stat-label" style={{ color: statusFilter === 'handover' ? '#e0e7ff' : '#4338ca' }}>
            Handovers to Me {handoverRequests.length > 0 && <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem' }}>Action</span>}
          </div>
        </div>
        {managerApprovals.length > 0 && (
          <div 
            className={`wrms-stat-card clickable ${statusFilter === 'manager' ? 'active' : ''}`}
            onClick={() => setStatusFilter('manager')}
            style={{
              background: statusFilter === 'manager' ? 'linear-gradient(135deg, #059669, #047857)' : '#ecfdf5',
              border: '1.5px solid #34d399',
              color: statusFilter === 'manager' ? '#ffffff' : '#065f46'
            }}
          >
            <div className="wrms-stat-icon" style={{ background: '#059669', color: '#ffffff' }}><FaCheck /></div>
            <div className="wrms-stat-number" style={{ color: statusFilter === 'manager' ? '#ffffff' : '#064e3b' }}>{managerApprovals.length}</div>
            <div className="wrms-stat-label" style={{ color: statusFilter === 'manager' ? '#d1fae5' : '#047857' }}>
              Manager Approvals <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem' }}>Action</span>
            </div>
          </div>
        )}
      </div>

      {/* Reporting Manager Alert Banner */}
      {managerApprovals.length > 0 && statusFilter !== 'manager' && (
        <div className="wrms-alert-banner" style={{
          background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
          border: '1.5px solid #6ee7b7',
          borderRadius: '14px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          boxShadow: '0 4px 12px rgba(5, 150, 105, 0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', flexShrink: 0 }}>
              <FaCheck size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#064e3b' }}>
                Requests Awaiting Your Approval as Reporting Manager ({managerApprovals.length})
              </div>
              <div style={{ fontSize: '0.8rem', color: '#047857', marginTop: '2px' }}>
                Your team members have submitted leave/permission requests awaiting your review and approval.
              </div>
            </div>
          </div>
          <button
            className="wrms-btn-primary"
            onClick={() => setStatusFilter('manager')}
            style={{ fontSize: '0.82rem', padding: '8px 18px', display: 'flex', alignItems: 'center', gap: '6px', background: '#059669', borderColor: '#059669' }}
          >
            Review Requests ({managerApprovals.length})
          </button>
        </div>
      )}

      {/* Handover Alert Banner */}
      {handoverRequests.length > 0 && statusFilter !== 'handover' && (
        <div className="wrms-alert-banner" style={{
          background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
          border: '1.5px solid #c7d2fe',
          borderRadius: '14px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', flexShrink: 0 }}>
              <FaUserFriends size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e1b4b' }}>
                Handover Requests Awaiting Your Approval ({handoverRequests.length})
              </div>
              <div style={{ fontSize: '0.8rem', color: '#4338ca', marginTop: '2px' }}>
                Colleagues have requested to hand over their responsibilities to you during their leave. Please review and accept.
              </div>
            </div>
          </div>
          <button
            className="wrms-btn-primary"
            onClick={() => setStatusFilter('handover')}
            style={{ fontSize: '0.82rem', padding: '8px 18px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            Review Handovers ({handoverRequests.length})
          </button>
        </div>
      )}

      <div className="wrms-dashboard-layout">
        {/* Left: Requests */}
        <div className="wrms-dashboard-main">
          {/* Recent Requests */}
          <div className="wrms-card">
            <div className="wrms-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ fontWeight: 700, margin: 0, color: '#0f172a' }}>
                {getHeaderLabel()}
                {(statusFilter !== 'all' || search || startDate || endDate) && (
                  <button 
                    className="btn btn-link btn-sm p-0 text-decoration-none ms-2"
                    onClick={() => {
                      setStatusFilter('all');
                      setSearch('');
                      setStartDate('');
                      setEndDate('');
                    }}
                    style={{ fontSize: '0.75rem', fontWeight: 600, color: '#4f46e5' }}
                  >
                    (Clear Filter)
                  </button>
                )}
              </h6>
              <button className="wrms-btn-outline" onClick={fetchData} style={{ padding: '5px 12px' }}>
                <FaSync size={11} /> Refresh
              </button>
            </div>
            <div className="wrms-card-body">
              {/* Responsive Filter controls */}
              <div className="wrms-emp-filter-bar">
                {/* Search & Dates */}
                <div className="wrms-emp-filter-left">
                  {/* Search input */}
                  <div className="wrms-admin-search-wrap" style={{ flex: 1, minWidth: '180px' }}>
                    <FaSearch className="wrms-search-icon" />
                    <input
                      className="wrms-form-control form-control"
                      placeholder="Search by ID, type, reason..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={{ fontSize: '0.82rem', height: '36px' }}
                    />
                  </div>
                  {/* Start & End Dates */}
                  <div className="wrms-emp-filter-dates">
                    <div className="wrms-date-field" style={{ flex: 1 }}>
                      <span className="wrms-date-label">From</span>
                      <input
                        type="date"
                        className="wrms-form-control form-control"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        style={{ fontSize: '0.82rem', height: '36px', padding: '6px 10px' }}
                      />
                    </div>
                    <div className="wrms-date-field" style={{ flex: 1 }}>
                      <span className="wrms-date-label">To</span>
                      <input
                        type="date"
                        className="wrms-form-control form-control"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        style={{ fontSize: '0.82rem', height: '36px', padding: '6px 10px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Grid / List Toggles */}
                <div className="wrms-view-toggle">
                  <button
                    className={layoutMode === 'grid' ? 'active' : ''}
                    onClick={() => setLayoutMode('grid')}
                    title="Grid View"
                  >
                    <FaThLarge size={13} />
                  </button>
                  <button
                    className={layoutMode === 'list' ? 'active' : ''}
                    onClick={() => setLayoutMode('list')}
                    title="List View"
                  >
                    <FaList size={13} />
                  </button>
                </div>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading requests...</div>
              ) : searchedRequests.length === 0 ? (
                <div className="wrms-empty-state">
                  <div className="wrms-empty-state-icon"><FaClipboardList size={36} color="#94a3b8" /></div>
                  <h5>No Requests Found</h5>
                  <p>{(statusFilter === 'all' && !search && !startDate && !endDate) ? 'Use the Quick Apply buttons to submit your first request.' : 'No requests matched your filter criteria.'}</p>
                </div>
              ) : layoutMode === 'grid' ? (
                <div className="wrms-request-grid">
                  {searchedRequests.map(req => (
                    <RequestCard
                      key={req.id}
                      request={req}
                      showEmployee={statusFilter === 'handover' || statusFilter === 'manager'}
                      layout="grid"
                      approverActions={statusFilter === 'handover' ? {
                        handleApprove: handleAcceptHandover,
                        handleReject: handleDeclineHandover,
                        processing: actionProcessing,
                        approveLabel: 'Accept Handover',
                        rejectLabel: 'Decline',
                      } : statusFilter === 'manager' ? {
                        handleApprove: handleApproveManagerRequest,
                        handleReject: handleRejectManagerRequest,
                        processing: actionProcessing,
                        approveLabel: 'Approve Request',
                        rejectLabel: 'Reject Request',
                      } : null}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {searchedRequests.map(req => (
                    <RequestCard
                      key={req.id}
                      request={req}
                      showEmployee={statusFilter === 'handover' || statusFilter === 'manager'}
                      layout="list"
                      approverActions={statusFilter === 'handover' ? {
                        handleApprove: handleAcceptHandover,
                        handleReject: handleDeclineHandover,
                        processing: actionProcessing,
                        approveLabel: 'Accept Handover',
                        rejectLabel: 'Decline',
                      } : statusFilter === 'manager' ? {
                        handleApprove: handleApproveManagerRequest,
                        handleReject: handleRejectManagerRequest,
                        processing: actionProcessing,
                        approveLabel: 'Approve Request',
                        rejectLabel: 'Reject Request',
                      } : null}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Quick Apply */}
        <div className="wrms-dashboard-sidebar">
          <div className="wrms-card" style={{ position: 'sticky', top: '80px' }}>
            <div className="wrms-card-header">
              <h6 style={{ fontWeight: 700, margin: 0, color: '#0f172a' }}>Quick Apply</h6>
            </div>
            <div className="wrms-card-body">
              <div className="wrms-quick-apply">
                {QUICK_APPLY.map(qa => {
                  const IconComp = qa.icon || FaClipboardList;
                  return (
                    <div
                      key={qa.type}
                      className="wrms-apply-btn"
                      onClick={() => navigate(qa.path)}
                    >
                      <span className="btn-icon"><IconComp size={16} /></span>
                      <span className="btn-label">{qa.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeWRMSDashboard;
